var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo, createContext, useState, forwardRef, } from "react";
import Box from "../box/Box";
import { StyledErrorIcon, StyledErrorIconContainer, StyledIcon, StyledInput } from "./Input.style";
import { useNumberEventHandlers } from "./useNumberEventHandlers";
import useUniqueId from "../../hooks/useUniqueId";
import { useInputAutoComplete } from "./useInputAutocomplete";
import { useInputValidation } from "./useInputValidation";
import { withPortal } from "../../hoc/withPortal";
import InputAutoCompleteDropdown from "./InputAutoCompleteDropdown";
import useIdAttrs from "../../hooks/useIdAttrs";
import { StyledHiddenScreenReaderOnly } from "../../utils/StyleUtils";
import useAutoFocus from "../../hooks/useAutoFocus";
import useForwardedRef from "../../hooks/useForwardedRef";
const prefixId = "nx-input-";
export const DEFAULT_DELAY_MS = 500;
// context to share between the input and the dropdown menu which autocomplete item is focused
export const InputContext = createContext([
    undefined,
    () => undefined,
]);
const InputContextProvider = (props) => {
    const focusedIndexState = useState(undefined);
    return React.createElement(InputContext.Provider, Object.assign({ value: focusedIndexState }, props));
};
const InternalInput = forwardRef(function InternalInput(_a, parentRef) {
    var { type = "text", id, "data-insights": insightsAttr, name, value, placeholder, maxLength, error, disabled = false, autoFocus: initialAutoFocus = false, width = "100%", onChange, onPressEnter, onFocus, onBlur, onKeyDown, size = "normal", prefix, suffix, min, max, step, readOnly, autoCompleteItems, onAutoCompleteSelect, show, hide, isVisible, forwardedRef, autoCompleteExtraTextMaxWidth } = _a, rest = __rest(_a, ["type", "id", "data-insights", "name", "value", "placeholder", "maxLength", "error", "disabled", "autoFocus", "width", "onChange", "onPressEnter", "onFocus", "onBlur", "onKeyDown", "size", "prefix", "suffix", "min", "max", "step", "readOnly", "autoCompleteItems", "onAutoCompleteSelect", "show", "hide", "isVisible", "forwardedRef", "autoCompleteExtraTextMaxWidth"]);
    const stringId = id;
    const idAttrs = useIdAttrs({ id: stringId, insightsAttr });
    const [ref, setRef] = useForwardedRef(parentRef);
    const autoFocus = initialAutoFocus && !disabled;
    useAutoFocus(ref, autoFocus);
    const numberInputProps = useNumberEventHandlers({
        type,
        value,
        max,
        min,
        onBlur,
        onKeyDown,
        onChange,
        step,
    });
    const handleKeyPress = (e) => {
        if (onPressEnter && e.key === "Enter") {
            onPressEnter(e);
        }
    };
    const onChangeWithValidation = useInputValidation({ type, onChange });
    const autoCompleteProps = useInputAutoComplete(ref, {
        id,
        autoCompleteItems,
        onAutoCompleteSelect,
        value,
        type,
        onChange: onChangeWithValidation,
        onBlur,
        onKeyDown,
        hide,
        show,
        isVisible,
    });
    const errorIdAttrs = idAttrs("-error");
    const hasErrorMessage = typeof error === "string";
    const renderError = (errorMessage, hasSuffix) => (React.createElement(StyledErrorIconContainer, Object.assign({}, errorIdAttrs, (errorMessage && { "aria-live": "polite" }), { hasSuffix: hasSuffix }),
        React.createElement(Box, { as: "span", display: "inline-block", width: "100%", height: "100%", title: errorMessage, "aria-hidden": "true" },
            React.createElement(StyledErrorIcon, null)),
        errorMessage && React.createElement(StyledHiddenScreenReaderOnly, null, errorMessage)));
    return (React.createElement(Box, { ref: forwardedRef, as: "span", display: "inline-block", maxWidth: "100%", position: "relative", width: width },
        prefix && React.createElement(StyledIcon, { icon: prefix, position: "prefix" }),
        React.createElement(StyledInput, Object.assign({}, idAttrs(), numberInputProps, autoCompleteProps, { ref: setRef, name: name, disabled: disabled, maxLength: maxLength, placeholder: placeholder, value: value, onChange: onChangeWithValidation, onKeyPress: onPressEnter && handleKeyPress, onFocus: onFocus, error: error, prefix: prefix, suffix: suffix, variant: size, readOnly: readOnly, "data-focus-border": true }, (error && Object.assign({ "aria-invalid": true }, (hasErrorMessage && { "aria-errormessage": errorIdAttrs.id }))), rest)),
        suffix && React.createElement(StyledIcon, { icon: suffix, position: "suffix" }),
        error && renderError(hasErrorMessage ? error : undefined, Boolean(suffix))));
});
const Input = forwardRef(function Input(props, ref) {
    const { autoCompleteItems } = props;
    const id = useUniqueId(prefixId, props.id);
    const InputComponent = useMemo(() => autoCompleteItems !== undefined
        ? withPortal(InputAutoCompleteDropdown, {
            trigger: "none",
            sameWidth: true,
            shouldCloseWhenTargetHidden: true,
        })(InternalInput)
        : InternalInput, [autoCompleteItems !== undefined]);
    return (React.createElement(InputContextProvider, null,
        React.createElement(InputComponent, Object.assign({ ref: ref }, props, { id: id }))));
});
export default Input;
//# sourceMappingURL=Input.js.map